// sites.$siteId.risks.$riskId.tsx — T046
// Route : /sites/:siteId/risks/:riskId → consultation, ou édition explicite.

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Suspense } from "react";
import { z } from "zod";

import { useDuerpVersionSnapshot } from "@/application/hooks/useDuerpVersionSnapshot";
import { usePermissions } from "@/application/hooks/usePermissions";
import {
  mapSnapshotRiskToRisk,
  type SnapshotVersionContext,
} from "@/application/utils/duerp-version-snapshot";
import type { Risk } from "@/domain/types/risk";
// eslint-disable-next-line no-restricted-imports -- route loaders call API directly (TanStack Router pattern)
import { getRisk } from "@/infrastructure/api/risks.api";
import { PageErrorFallback } from "@/presentation/components/shared/PageErrorFallback";
import { RiskFormPage } from "@/presentation/pages/RiskFormPage";
import { LoadingSpinner } from "@/shared/components/LoadingSpinner";

import { Route as SiteRoute } from "./sites.$siteId";

const riskDetailSearchSchema = z.object({
  mode: z.literal("edit").optional(),
  versionId: z.string().min(1).optional(),
  workUnitId: z.string().min(1).optional(),
  riskCategory: z.enum(["Physical", "Psychosocial"]).optional(),
  riskId: z.string().min(1).optional(),
});

interface RiskDetailSearch {
  mode?: "edit";
  versionId?: string;
  workUnitId?: string;
  riskCategory?: "Physical" | "Psychosocial";
  riskId?: string;
}

interface RiskDetailContentProps {
  readonly siteId: string;
  readonly regionId: string;
  readonly riskId: string;
  readonly liveRisk: Risk | null;
  readonly mode?: "edit";
  readonly versionId?: string;
  readonly workUnitId?: string;
  readonly riskCategory?: "Physical" | "Psychosocial";
}

function buildRiskContextSearch(
  versionId?: string,
  workUnitId?: string,
  riskCategory?: "Physical" | "Psychosocial",
  riskId?: string,
): Pick<
  RiskDetailSearch,
  "versionId" | "workUnitId" | "riskCategory" | "riskId"
> | undefined {
  if (!versionId && !workUnitId && !riskCategory && !riskId) return undefined;

  return {
    ...(versionId ? { versionId } : {}),
    ...(workUnitId ? { workUnitId } : {}),
    ...(riskCategory ? { riskCategory } : {}),
    ...(riskId ? { riskId } : {}),
  };
}

function buildRiskEditSearch(
  versionId?: string,
  workUnitId?: string,
  riskCategory?: "Physical" | "Psychosocial",
): RiskDetailSearch {
  return {
    mode: "edit",
    ...(versionId ? { versionId } : {}),
    ...(workUnitId ? { workUnitId } : {}),
    ...(riskCategory ? { riskCategory } : {}),
  };
}

function SnapshotRiskDetails({
  siteId,
  riskId,
  versionId,
  workUnitId,
  riskCategory,
  canEditCurrentRisk,
}: Pick<RiskDetailContentProps, "siteId" | "riskId"> & {
  readonly versionId: string;
  readonly workUnitId?: string;
  readonly riskCategory?: "Physical" | "Psychosocial";
  readonly canEditCurrentRisk: boolean;
}) {
  const navigate = useNavigate();
  const snapshotQuery = useDuerpVersionSnapshot(siteId, versionId);

  if (snapshotQuery.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner label="Chargement de la version DUERP…" />
      </div>
    );
  }

  if (snapshotQuery.isError || !snapshotQuery.data) {
    return (
      <PageErrorFallback
        status={snapshotQuery.error ? 500 : 404}
        onRetry={() => void snapshotQuery.refetch()}
      />
    );
  }

  const snapshotRisk = snapshotQuery.data.risks.find(
    (entry) => entry.riskId === riskId,
  );
  if (!snapshotRisk) {
    return (
      <PageErrorFallback
        status={404}
        onRetry={() => void snapshotQuery.refetch()}
      />
    );
  }

  const version: SnapshotVersionContext = {
    id: snapshotQuery.data.versionId,
    siteId: snapshotQuery.data.siteId,
    year: snapshotQuery.data.year,
    status: snapshotQuery.data.status,
    validatedAt: null,
    validatedBy: null,
    createdAt: "",
    createdBy: "",
  };
  const risk = mapSnapshotRiskToRisk(
    snapshotRisk,
    siteId,
    version,
  );
  const canEdit = canEditCurrentRisk && version.status === "Draft";

  return (
    <RiskFormPage
      siteId={siteId}
      risk={risk}
      isReadOnly
      onEdit={
        canEdit
          ? () =>
              void navigate({
                to: "/sites/$siteId/risks/$riskId",
                params: { siteId, riskId },
                search: buildRiskEditSearch(
                  versionId,
                  workUnitId,
                  riskCategory,
                ),
              })
          : undefined
      }
      versionContext={{ year: version.year, status: version.status }}
      measuresOverride={snapshotRisk.measures}
      onBack={() =>
        void navigate({
          to: "/sites/$siteId/risks",
          params: { siteId },
          search: {
            versionId,
            ...(workUnitId ? { workUnitId } : {}),
            ...(riskCategory ? { riskCategory } : {}),
            riskId,
          },
        })
      }
    />
  );
}

function RiskDetailContent({
  siteId,
  regionId,
  riskId,
  liveRisk,
  mode,
  versionId,
  workUnitId,
  riskCategory,
}: RiskDetailContentProps) {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const canEditCurrentRisk = permissions.duerp.canEditRisk(siteId, regionId);
  if (versionId && mode !== "edit") {
    return (
      <SnapshotRiskDetails
        siteId={siteId}
        riskId={riskId}
        versionId={versionId}
        workUnitId={workUnitId}
        riskCategory={riskCategory}
        canEditCurrentRisk={canEditCurrentRisk}
      />
    );
  }

  if (!liveRisk) {
    return <PageErrorFallback status={404} />;
  }

  const contextWorkUnitId = workUnitId ?? liveRisk.workUnitId;
  const contextRiskCategory = riskCategory ?? liveRisk.family?.category;

  if (mode !== "edit") {
    return (
      <RiskFormPage
        siteId={siteId}
        risk={liveRisk}
        isReadOnly
        onEdit={
          canEditCurrentRisk && !liveRisk.isLocked
            ? () =>
                void navigate({
                  to: "/sites/$siteId/risks/$riskId",
                  params: { siteId, riskId },
                  search: buildRiskEditSearch(
                    versionId,
                    contextWorkUnitId,
                    contextRiskCategory,
                  ),
                })
            : undefined
        }
        onBack={() =>
          void navigate({
            to: "/sites/$siteId/risks",
            params: { siteId },
            search: buildRiskContextSearch(
              versionId,
              contextWorkUnitId,
              contextRiskCategory,
              riskId,
            ),
          })
        }
      />
    );
  }

  return (
    <RiskFormPage
      siteId={siteId}
      risk={liveRisk}
      isReadOnly={!canEditCurrentRisk}
      onBack={() =>
        void navigate({
          to: "/sites/$siteId/risks",
          params: { siteId },
          search: buildRiskContextSearch(
            versionId,
            contextWorkUnitId,
            contextRiskCategory,
            riskId,
          ),
        })
      }
    />
  );
}

export const Route = createFileRoute("/sites/$siteId/risks/$riskId")({
  validateSearch: (search: Record<string, unknown>): RiskDetailSearch => {
    const parsed = riskDetailSearchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  loaderDeps: ({ search }) => ({
    mode: search.mode,
    versionId: search.versionId,
  }),
  loader: ({ params, deps }) =>
    deps.versionId && deps.mode !== "edit"
      ? null
      : getRisk(params.siteId, params.riskId),
  component: function RiskDetailRouteComponent() {
    const { siteId, riskId } = Route.useParams();
    const { site } = SiteRoute.useLoaderData();
    const liveRisk = Route.useLoaderData();
    const { mode, versionId, workUnitId, riskCategory } = Route.useSearch();

    return (
      <Suspense
        fallback={
          <div className="flex h-64 items-center justify-center">
            <LoadingSpinner label="Chargement du risque…" />
          </div>
        }
      >
        <RiskDetailContent
          siteId={siteId}
          regionId={site.regionId}
          riskId={riskId}
          liveRisk={liveRisk}
          mode={mode}
          versionId={versionId}
          workUnitId={workUnitId}
          riskCategory={riskCategory}
        />
      </Suspense>
    );
  },
});
